import React from "react";
import {
  Building2,
  FactoryIcon,
  FileStack,
  Wallet,
  MapPinned,
  Network,
  Layers,
  Shield,
  Smartphone,
  Globe2,
  Boxes,
  ArrowRight,
  ArrowUpRight,
  Activity,
  Languages,
} from "lucide-react";

// A composed dashboard mockup built with Tailwind + Lucide (no images)
const InstitutionalMockup = () => (
  <div
    aria-hidden="true"
    data-testid="hero-mockup"
    className="relative bg-white border border-ink-200 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.18)]"
  >
    {/* Top bar */}
    <div className="flex items-center justify-between border-b border-ink-200 px-4 py-2.5">
      <div className="flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-ink-200" />
        <div className="w-2.5 h-2.5 rounded-full bg-ink-200" />
        <div className="w-2.5 h-2.5 rounded-full bg-ink-200" />
      </div>
      <div className="font-mono text-[10px] tracking-widest uppercase text-ink-500">
        allinstra · institutional console
      </div>
      <div className="font-mono text-[10px] text-ink-400">illustrative</div>
    </div>

    {/* Content */}
    <div className="grid grid-cols-12">
      {/* Sidebar */}
      <aside className="col-span-3 border-r border-ink-200 p-4 bg-ink-50/40">
        <div className="tag-overline">Modules</div>
        <ul className="mt-4 space-y-2 text-[12px] text-ink-700">
          {[
            ["Municipality", Building2, true],
            ["Industrial Zone", FactoryIcon, false],
            ["Documents", FileStack, false],
            ["Finance", Wallet, false],
            ["Field Ops", MapPinned, false],
            ["Portals", Network, false],
          ].map(([label, Icon, active]) => (
            <li
              key={label}
              className={`flex items-center gap-2 px-2 py-1.5 ${
                active ? "bg-white border border-ink-200 text-ink-900" : ""
              }`}
            >
              <Icon size={13} className={active ? "text-cyan-600" : "text-ink-500"} />
              <span>{label}</span>
              {active && <span className="ml-auto w-1.5 h-1.5 bg-cyan-500" />}
            </li>
          ))}
        </ul>
      </aside>

      {/* Main */}
      <div className="col-span-9 p-5">
        <div className="grid grid-cols-3 gap-3">
          {[
            { k: "Active Workflows", v: "1,284", d: "+4.2%" },
            { k: "Documents Routed", v: "38,901", d: "+1.1%" },
            { k: "Subscribers", v: "126,540", d: "+0.6%" },
          ].map((s) => (
            <div key={s.k} className="border border-ink-200 p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-ink-500">
                {s.k}
              </div>
              <div className="mt-2 font-display text-xl font-semibold text-ink-900">{s.v}</div>
              <div className="text-[10px] text-cyan-700 mt-0.5">{s.d}</div>
            </div>
          ))}
        </div>

        {/* Chart placeholder */}
        <div className="mt-3 border border-ink-200 p-4">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-500">
              Institutional Workflow — last 12 weeks
            </div>
            <div className="flex items-center gap-2 text-[10px] text-ink-500">
              <span className="w-2 h-2 bg-ink-900" /> Routed
              <span className="w-2 h-2 bg-cyan-500 ml-2" /> Approved
            </div>
          </div>
          <svg viewBox="0 0 320 90" className="mt-3 w-full h-24" preserveAspectRatio="none">
            <polyline
              points="0,70 30,60 60,62 90,48 120,52 150,40 180,42 210,28 240,32 270,22 300,18 320,12"
              fill="none"
              stroke="#0F172A"
              strokeWidth="1.5"
            />
            <polyline
              points="0,80 30,76 60,72 90,68 120,66 150,58 180,56 210,48 240,42 270,38 300,32 320,28"
              fill="none"
              stroke="#06B6D4"
              strokeWidth="1.5"
            />
          </svg>
        </div>

        {/* Mini list */}
        <div className="mt-3 grid grid-cols-2 gap-3">          <div className="border border-ink-200">
            <div className="px-3 py-2 border-b border-ink-200 font-mono text-[10px] uppercase tracking-widest text-ink-500">
              Document Flow
            </div>
            <ul className="text-[12px] text-ink-700 divide-y divide-ink-100">
              {["Inbound", "Routing", "Approval", "Archive"].map((s, i) => (
                <li key={s} className="flex items-center justify-between px-3 py-2">
                  <span>{s}</span>
                  <span className="font-mono text-[10px] text-ink-500">
                    {[214, 187, 142, 96][i]}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-ink-200">
            <div className="px-3 py-2 border-b border-ink-200 font-mono text-[10px] uppercase tracking-widest text-ink-500">
              System Layers
            </div>
            <ul className="text-[12px] text-ink-700 divide-y divide-ink-100">
              {[
                "Workflow layer",
                "Document layer",
                "Service layer",
                "Reporting layer",
              ].map((k) => (
                <li key={k} className="flex items-center justify-between px-3 py-2">
                  <span>{k}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-ink-400">
                    illustrative
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div className="border-t border-ink-200 px-4 py-2 flex items-center justify-between">
      <div className="font-mono text-[9.5px] uppercase tracking-widest text-ink-500">
        Illustrative institutional console
      </div>
      <div className="font-mono text-[9.5px] text-ink-400">indicators are illustrative</div>
    </div>
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-white border-b border-ink-200">
    <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-16 lg:pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="tag-overline" data-testid="hero-overline">
            Allinstra · Institutional Systems
          </div>
          <h1
            data-testid="hero-title"
            className="heading-display mt-5 text-[44px] sm:text-6xl lg:text-7xl"
          >
            <span className="font-light text-ink-700">All institutions,</span>
            <br />
            <span className="font-bold text-ink-900">one structure.</span>
          </h1>
          <p
            data-testid="hero-lead"
            className="mt-7 max-w-xl text-base sm:text-lg text-ink-600 leading-relaxed"
          >
            Digital management systems for municipalities, organized industrial zones,
            public-service organizations, and structured institutions that need clearer
            operations, stronger document control, connected workflows, and better decision
            support.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-cyan-700 font-mono uppercase text-[11px] tracking-widest">
            <span className="w-6 h-px bg-cyan-500" /> Bring order forward.
          </div>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href="/solutions" data-testid="hero-cta-solutions" className="btn-primary">
              Explore Solutions <ArrowUpRight size={16} />
            </a>
            <a
              href="/product-portfolio"
              data-testid="hero-cta-portfolio"
              className="btn-secondary"
            >
              View Product Portfolio <ArrowRight size={16} />
            </a>
            <a href="/contact" data-testid="hero-cta-contact" className="btn-ghost">
              Contact Us <ArrowRight size={14} />
            </a>
          </div>
        </div>
        <div className="lg:col-span-5">
          <InstitutionalMockup />
        </div>
      </div>
    </div>
  </section>
);

const WhatAllinstraDoes = () => (
  <section
    data-testid="section-what-allinstra-does"
    className="bg-ink-50 border-t border-ink-200"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="tag-overline">01 · What Allinstra Does</div>
          <h2 className="heading-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold">
            <span className="font-light">One structure for </span>
            <span className="font-bold">complex institutional operations.</span>
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Allinstra is designed for institutions that need structure beyond standalone
            software.
          </p>
          <p>
            It brings operational workflows, documents, records, service processes, utility
            subscribers, finance and revenue activities, procurement, field operations,
            portals, integrations, and management visibility into a more connected digital
            environment.
          </p>
          <p className="text-ink-900">
            The result is a clearer institutional operating structure, built to improve
            traceability, coordination, control, and long-term digital continuity.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const SOLUTION_PATHWAYS = [
  {
    icon: Building2,
    title:
      "For Universities, Hospitals, Chambers, Unions & Multi-Department Institutions",
    body:
      "For universities, hospitals, professional chambers, business associations, unions, public-interest organizations, large campuses, and multi-department institutions that need clearer coordination across departments, services, documents, internal workflows, assets, reporting, and management visibility.",
  },
  {
    icon: Layers,
    title: "For Municipalities & Local Public Administrations",
    body:
      "For municipalities, local administrations, public-service organizations, local unions, and similar institutions that need clearer service coordination, citizen interaction, departmental visibility, document continuity, finance workflows, field operations, and management reporting.",
  },
  {
    icon: FactoryIcon,
    title: "For Industrial Zones, Utilities & Service Operators",
    body:
      "For organized industrial zones, business parks, utility-based institutions, service operators, infrastructure-linked environments, and estate-style operating structures that need stronger control over subscribers, services, consumption, field work, payments, portals, and operational visibility.",
  },
  {
    icon: FileStack,
    title: "For Document-Intensive & Regulated Service Environments",
    body:
      "For institutions where documents, approvals, archives, correspondence, records, verification, access rights, and traceability are central to daily operations. This pathway is relevant for public institutions, universities, hospitals, clinics, laboratories, chambers, unions, regulated service providers, professional organizations, and other document-heavy environments.",
  },
  {
    icon: Wallet,
    title: "For Finance, Revenue & Administrative Operations",
    body:
      "For internal departments and administrative teams that need stronger structure across revenue, accrual, collection, accounting, procurement, inventory, assets, vehicles, administrative affairs, legal processes, human resources, and institutional reporting.",
  },
  {
    icon: MapPinned,
    title: "For Field, Facility, Laboratory & Technical Operations",
    body:
      "For institutions that manage field teams, facilities, infrastructure, technical tasks, laboratories, sample processes, maintenance, inspections, spatial records, mobile work orders, and service execution outside the central office.",
  },
  {
    icon: Network,
    title: "For Portals, Integrations & Executive Visibility",
    body:
      "For institutions that need clearer digital access channels, internal portals, user-facing interfaces, external integrations, mobile access, notifications, reporting structures, dashboards, and executive visibility.",
  },
];

const SolutionPathways = () => (
  <section data-testid="section-solution-pathways" className="bg-white border-t border-ink-200">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="tag-overline">02 · Solution Pathways</div>
        <h2 className="heading-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold">
          Solutions shaped around{" "}
          <span className="text-cyan-700">institutional realities</span>.
        </h2>
        <div className="mt-5 space-y-4 text-ink-600 text-base sm:text-lg leading-relaxed">
          <p>
            Allinstra solutions are organized around the institutions, teams, and operational
            contexts that need stronger structure.
          </p>
          <p>
            Instead of starting with software modules, this section starts with the type of
            organization, department, or operational challenge you represent — and shows how
            Allinstra’s product portfolio can be shaped around that need.
          </p>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SOLUTION_PATHWAYS.map((s) => (
          <div
            key={s.title}
            data-testid={`solution-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="bg-white border border-ink-200 p-7 sm:p-8 card-hover"
          >
            <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600 mb-5">
              <s.icon size={18} strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-lg sm:text-xl font-semibold text-ink-900 tracking-tight">
              {s.title}
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-600">{s.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <a href="/solutions" data-testid="solutions-cta" className="btn-ghost">
          Explore all solutions <ArrowRight size={14} />
        </a>
      </div>
    </div>
  </section>
);

const PORTFOLIO_FAMILIES = [
  {
    n: "01",
    icon: Layers,
    title: "Institutional Management Platforms",
    body:
      "Core management information systems, public administration platforms, executive dashboards, service definitions, meeting management, quality management, and strategic planning structures.",
  },
  {
    n: "02",
    icon: Building2,
    title: "Municipality & Public Service Systems",
    body:
      "Digital modules for municipal services, citizen relationship management, public service desks, fire department operations, municipal police, health services, culture, sports, libraries, parks, cemeteries, marriage services, transportation, and related public-service functions.",
  },
  {
    n: "03",
    icon: FactoryIcon,
    title: "Industrial Zone & Utility Management",
    body:
      "Systems for organized industrial zones, subscriber management, electricity, water, natural gas, industrialist portals, prepaid meters, automated meter reading, mobile meter reading, work orders, payment points, and direct debit structures.",
  },
  {
    n: "04",
    icon: FileStack,
    title: "Document, Records & Digital Transformation",
    body:
      "Electronic document management, digital archive, document verification, electronic signature-ready processes, registered electronic mail readiness, e-archive, e-invoice, e-ledger, archive scanning, and document tracking capabilities.",
  },
  {
    n: "05",
    icon: Wallet,
    title: "Finance, Revenue & Procurement",
    body:
      "Revenue, accrual, collection, accounting, analytical accounting, procurement, direct purchasing, inventory, movable asset management, vehicle management, online collection, and financial process integration.",
  },
  {
    n: "06",
    icon: MapPinned,
    title: "Field, Infrastructure & Spatial Operations",
    body:
      "Zoning, address numbering, licensing, real estate records, land registry integration readiness, geographic information system readiness, fault management, maintenance and repair, laboratory processes, wastewater discharge and analysis, and field data collection.",
  },
  {
    n: "07",
    icon: Network,
    title: "Portals, Integrations & External Systems",
    body:
      "Institutional web portals, mobile applications, call center integration, SMS notifications, identity and address data integrations, e-government readiness, payment systems, direct debit, single sign-on, and external system communication.",
  },
];

const PortfolioPreview = () => (
  <section data-testid="section-portfolio" className="bg-ink-50 border-t border-ink-200">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="tag-overline">03 · Product Portfolio Preview</div>
          <h2 className="heading-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold">
            A modular portfolio for{" "}
            <span className="font-light italic text-ink-700">institutional scale.</span>
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-4 text-ink-600 text-[15px] sm:text-base leading-relaxed">
          <p>
            Allinstra’s Product Portfolio presents the complete institutional product and
            module architecture behind the solution pathways.
          </p>
          <p>
            While Solutions are organized around institution types, operational scenarios, and
            practical use cases, Product Portfolio is organized by product family and module
            structure.
          </p>
          <p className="text-ink-900">
            The portfolio is structured into seven product families that can be evaluated
            independently or combined as part of a wider institutional implementation.
          </p>
        </div>
      </div>

      <div className="mt-14 border border-ink-200 bg-white">
        {PORTFOLIO_FAMILIES.map((f, i) => (
          <div
            key={f.title}
            data-testid={`portfolio-row-${f.n}`}
            className={`grid grid-cols-1 md:grid-cols-12 gap-6 p-6 sm:p-8 ${
              i !== PORTFOLIO_FAMILIES.length - 1 ? "border-b border-ink-200" : ""
            }`}
          >
            <div className="md:col-span-1 font-mono text-xs text-cyan-700 uppercase tracking-widest">
              {f.n}
            </div>
            <div className="md:col-span-3 flex items-start gap-3">
              <div className="w-9 h-9 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <f.icon size={16} strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-base sm:text-lg font-semibold text-ink-900 tracking-tight">
                {f.title}
              </h3>
            </div>
            <p className="md:col-span-8 text-[14.5px] text-ink-600 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <a href="/product-portfolio" data-testid="portfolio-cta" className="btn-primary">
          View Full Product Portfolio <ArrowUpRight size={16} />
        </a>
      </div>
    </div>
  </section>
);

const TECH_FOUNDATIONS = [
  ["Web-Based Institutional Access", Globe2],
  ["Modular Enterprise Structure", Layers],
  ["Service-Oriented Integration Logic", Network],
  ["Role-Based Control and Authorization", Shield],
  ["Traceability, Logging and Accountability", Activity],
  ["Mobile, Portal and User Access Layers", Smartphone],
  ["Localization and Multilingual Readiness", Languages],
  ["Flexible Deployment Planning", Boxes],
];

const TechnologyFoundation = () => (
  <section data-testid="section-technology" className="bg-white border-t border-ink-200">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="tag-overline">04 · Technology Foundation</div>
          <h2 className="heading-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold">
            Built for <span className="font-light">integration, continuity,</span>{" "}
            <span className="font-bold">and institutional control.</span>
          </h2>
          <div className="mt-5 space-y-4 text-ink-600 text-[15px] leading-relaxed">
            <p>
              Allinstra is designed for institutions that need dependable digital structure
              across workflows, documents, records, services, field operations, integrations,
              and management visibility.
            </p>
            <p>
              The technology approach is modular, integration-aware, and built for long-term
              institutional use. It supports web-based access, role-based control, traceable
              process movement, service-oriented integration, localization, mobile and portal
              access, and flexible deployment planning.
            </p>
          </div>
          <a href="/technology" data-testid="technology-cta" className="btn-secondary mt-8">
            Explore Technology <ArrowRight size={14} />
          </a>
        </div>
        <div className="lg:col-span-7">
          <ul className="grid grid-cols-1 sm:grid-cols-2 border border-ink-200">
            {TECH_FOUNDATIONS.map(([label, Icon], i) => (
              <li
                key={label}
                data-testid={`home-tech-foundation-${i + 1}`}
                className={`flex items-start gap-3 p-5 sm:p-6 border-ink-200 ${
                  i % 2 === 0 ? "sm:border-r" : ""
                } ${i < TECH_FOUNDATIONS.length - 2 ? "border-b" : ""}`}
              >
                <div className="text-cyan-600 mt-0.5">
                  <Icon size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-mono text-[10px] text-ink-500 uppercase tracking-widest">
                    {String(i + 1).padStart(2, "0")} / 08
                  </div>
                  <div className="text-[14px] text-ink-900 mt-1 font-medium">{label}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const LocalRealities = () => (
  <section data-testid="section-local" className="bg-ink-900 text-ink-100 relative overflow-hidden">
    <div className="absolute inset-0 bg-grid-dark opacity-60" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="tag-overline" style={{ color: "#67E8F9" }}>
            05 · Built for Local Realities
          </div>
          <h2 className="heading-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
            Designed for adaptation, not assumption.
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-300">
          <p>
            Institutional systems must reflect the reality of each country, sector, organization,
            and legal environment.
          </p>
          <p>
            Some modules may depend on local legislation, public infrastructure, electronic
            government services, identity and address systems, payment infrastructure,
            electronic signature availability, third-party access, and country-specific
            integrations.
          </p>
          <p className="text-white">
            Allinstra evaluates each market and institution before implementation. The solution
            scope is defined according to what is available, what is legally possible, what is
            technically feasible, and what creates real institutional value.
          </p>
          <p>
            This approach allows Allinstra to support both mature digital environments and
            markets where institutional digital infrastructure is still developing.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Markets = () => (
  <section data-testid="section-markets" className="bg-white border-t border-ink-200">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="tag-overline">06 · Markets & Expansion Regions</div>
        <h2 className="heading-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold">
          Structured for selected markets and{" "}
          <span className="text-cyan-700">regional growth.</span>
        </h2>
        <p className="mt-5 text-ink-600 text-base sm:text-lg leading-relaxed">
          Allinstra is designed for institutions that require stronger operational management,
          document control, workflow visibility, and integrated digital structures. The initial
          market focus includes Kosovo, Türkiye, and the United Kingdom, while the wider
          expansion logic covers selected regional and international opportunities.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            t: "Kosovo",
            d:
              "Kosovo is a strategic market for Allinstra’s institutional systems approach. The country presents a clear opportunity for municipalities, public-service organizations, organized industrial zones, and structured institutions that want to improve operational visibility, document control, workflow discipline, and phased digital management capacity.",
          },
          {
            t: "Türkiye",
            d:
              "Türkiye represents an important reference and experience market for Allinstra’s institutional systems approach. Its public-service environments, municipality operations, organized industrial zones, utility-based structures, electronic document practices, and e-transformation experience provide a strong background for understanding complex institutional software needs.",
          },
          {
            t: "United Kingdom",
            d:
              "The United Kingdom represents a selective market opportunity for Allinstra’s institutional systems approach, particularly in environments where structured organizations need stronger workflow visibility, document and records control, operational coordination, reporting discipline, and modular management systems.",
          },
        ].map((m) => (
          <div key={m.t} className="bg-white border border-ink-200 p-7 sm:p-8 card-hover">
            <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-700">
              Core Market
            </div>
            <h3 className="font-display text-2xl font-semibold text-ink-900 mt-2 tracking-tight">
              {m.t}
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-600">{m.d}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 border border-ink-200 p-7 sm:p-8 bg-ink-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-4">
            <div className="tag-overline">Regional & International</div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-ink-900 mt-2 tracking-tight">
              Expansion Potential
            </h3>
          </div>
          <p className="lg:col-span-8 text-[15px] leading-relaxed text-ink-700">
            Allinstra may also support selected opportunities across the European Union, the
            Balkans, Turkic States, and other markets where institutional digital management
            systems are needed — depending on local legislation, third-party access, public
            infrastructure, institutional demand, and implementation feasibility.
          </p>
        </div>
      </div>

      <div className="mt-10">
        <a href="/markets" data-testid="markets-cta" className="btn-secondary">
          Discuss your market <ArrowRight size={14} />
        </a>
      </div>
    </div>
  </section>
);

const IMPLEMENTATION_STEPS = [
  ["Institutional needs assessment", "Operational understanding before scope."],
  ["Scope and module selection", "Choose what creates institutional value."],
  ["Local regulatory and infrastructure review", "Adaptation to legal and technical context."],
  ["Integration feasibility analysis", "Determine third-party and external access."],
  ["Configuration and localization", "Tune the system to country and sector reality."],
  ["Training and pilot use", "Build internal readiness through phased adoption."],
  ["Go-live and support", "Structured operational handover and stabilization."],
  ["Continuous improvement", "Refine modules as institutional maturity grows."],
];

const Implementation = () => (
  <section data-testid="section-implementation" className="bg-ink-50 border-t border-ink-200">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="tag-overline">07 · Implementation Approach</div>
          <h2 className="heading-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold">
            From <span className="font-light">product capability</span> to{" "}
            <span className="font-bold">institutional value.</span>
          </h2>
          <p className="mt-5 text-ink-600 text-[15px] leading-relaxed">
            Allinstra implementation is not limited to software delivery. Each project begins
            with institutional understanding, operational review, module selection, localization
            needs, integration feasibility, and phased implementation planning. This structured
            approach helps institutions adopt digital systems in a controlled, realistic, and
            sustainable way.
          </p>
          <a href="/implementation" data-testid="implementation-cta" className="btn-secondary mt-8">
            See implementation approach <ArrowRight size={14} />
          </a>
        </div>
        <div className="lg:col-span-7">
          <ol className="border border-ink-200 bg-white">
            {IMPLEMENTATION_STEPS.map(([t, d], i) => (
              <li
                key={t}
                className={`grid grid-cols-12 gap-4 p-5 sm:p-6 ${
                  i !== IMPLEMENTATION_STEPS.length - 1 ? "border-b border-ink-200" : ""
                }`}
              >
                <div className="col-span-2 sm:col-span-1 font-mono text-cyan-700 text-xs">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-10 sm:col-span-11">
                  <div className="font-display text-base sm:text-lg font-semibold text-ink-900 tracking-tight">
                    {t}
                  </div>
                  <div className="text-[14px] text-ink-600 mt-1">{d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section data-testid="section-final-cta" className="bg-ink-900 text-white relative overflow-hidden">
    <div className="absolute inset-0 bg-grid-dark opacity-50" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
      <div className="tag-overline mx-auto" style={{ color: "#67E8F9" }}>
        Final · Bring order forward
      </div>
      <h2 className="heading-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold text-white max-w-4xl mx-auto">
        Bring order forward with <span className="text-cyan-400">Allinstra</span>.
      </h2>
      <p className="mt-6 text-ink-300 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
        Whether you represent a municipality, an organized industrial zone, a university, a
        hospital, a chamber, a union, a public-service organization, a structured institution,
        or a potential institutional market, Allinstra can help you evaluate the right digital
        structure for your operational needs.
      </p>
      <p className="mt-8 font-display text-2xl sm:text-3xl text-white tracking-tight">
        All institutions, one structure.
      </p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <a
          href="/contact"
          data-testid="final-cta-contact"
          className="px-6 py-3.5 bg-cyan-500 text-ink-900 font-medium hover:bg-cyan-400 transition-colors inline-flex items-center gap-2"
        >
          Contact Us <ArrowUpRight size={16} />
        </a>
        <a
          href="/product-portfolio"
          data-testid="final-cta-portfolio"
          className="px-6 py-3.5 bg-transparent border border-ink-700 text-white hover:border-cyan-400 transition-colors inline-flex items-center gap-2"
        >
          View Product Portfolio <ArrowRight size={16} />
        </a>
        <a
          href="/solutions"
          data-testid="final-cta-solutions"
          className="px-6 py-3.5 bg-transparent text-white hover:text-cyan-300 transition-colors inline-flex items-center gap-2"
        >
          Explore Solutions <ArrowRight size={16} />
        </a>
      </div>
    </div>
  </section>
);

const Home = () => (
  <main data-testid="page-home">
    <Hero />
    <WhatAllinstraDoes />
    <SolutionPathways />
    <PortfolioPreview />
    <TechnologyFoundation />
    <LocalRealities />
    <Markets />
    <Implementation />
    <FinalCTA />
  </main>
);

export default Home;
